import { Validators } from '@angular/forms';
import { isMasked } from '@miq/utils';
import { LeaseColumns } from '@miq/catalog';
import { TenantSpaceClass } from '../../../../../../models/space/tenant-space.class';
import { formatDate, generateLastUpdatedByMessage } from '../../../../../../shared/utils/functions';
import { SuiteType } from '../../item.model';
import { DetailsOverlayValidators } from '../detail-overlay.validators';
import { AreaUrl } from '../details-overlay.constants';
import { DetailsOverlayData, SubFieldConfig } from '../details-overlay.model';

export const LEASE_CONFIG_ESTIMATE: { [key: string]: SubFieldConfig<TenantSpaceClass> & { condition: Function } } = {
  leaseStartDate: {
    condition: columns => {
      return columns
        .find(col => col.columnId === LeaseColumns.Terms_StartDate)
        ?.related?.find(col => col.columnId === LeaseColumns.Terms_StartDateEstimatedF);
    },
    dataField: 'leaseStartDateEstF',
    inputType: 'dropdown',
    formGroup: 'formGroupLeaseDate',
    lookupCategory: '/lookup/estimatedflag',
    displayFn: space => {
      const { startDateFlag } = space.getSpaceDatesEstF();
      return startDateFlag;
    },
  },
  leaseEndDate: {
    condition: columns => {
      return columns
        .find(col => col.columnId === LeaseColumns.Terms_EndDate)
        ?.related?.find(col => col.columnId === LeaseColumns.Terms_EndDateEstimatedF);
    },
    dataField: 'leaseEndDateEstF',
    inputType: 'dropdown',
    formGroup: 'formGroupLeaseDate',
    lookupCategory: '/lookup/estimatedflag',
    displayFn: space => {
      const { endDateFlag } = space.getSpaceDatesEstF();
      return endDateFlag;
    },
  },
  rentRate: {
    condition: columns => {
      return columns
        .find(col => col.columnId === LeaseColumns.Terms_BaseRentMonthly)
        ?.related?.find(col => col.columnId === LeaseColumns.Terms_BaseRentMonthlyEstimatedF);
    },
    dataField: 'rentRateEstF',
    inputType: 'dropdown',
    formGroup: 'formGroupRentRate',
    lookupCategory: '/lookup/estimatedflag',
    displayFn: space => {
      const { rentRateFlag } = space.getSpaceDatesEstF();
      return rentRateFlag;
    },
  },
  spaceSize: {
    condition: columns => {
      return columns
        .find(col => col.columnId === LeaseColumns.LeaseCompDetails_AreaLeased)
        ?.related?.find(col => col.columnId === LeaseColumns.LeaseCompDetails_AreaLeasedEstimatedF);
    },
    dataField: 'spaceSizeEstF',
    inputType: 'dropdown',
    formGroup: 'formGroupSpace',
    lookupCategory: '/lookup/estimatedflag',
    displayFn: space => {
      const { spaceSizeFlag } = space.getSpaceDatesEstF();
      return spaceSizeFlag;
    },
  },
};

export const LEASE_CONFIG: DetailsOverlayData<TenantSpaceClass> = {
  id: space => space.tenantId.toString(),
  title: space => space.companyName,
  progressBar: true,
  validators: [
    {
      groupName: 'formGroupSpace',
      validator: [DetailsOverlayValidators.notEmptySpaceUOM, DetailsOverlayValidators.spaceSizeLessThanFloorSize],
    },
    { groupName: 'formGroupRentRate', validator: [DetailsOverlayValidators.notEmptyRentUOM] },
    { groupName: 'formGroupLeaseDate', validator: [DetailsOverlayValidators.leaseDateValidator] },
  ],
  subsets: [
    {
      labelFn: columns => {
        return columns.find(col => col.columnId === LeaseColumns.FloorSuite_Suite)?.header;
      },
      dataField: 'suiteNumber',
      inputType: 'textbox',
      displayFn: space => space.suiteNumber,
      validators: [Validators.required],
      class: 'suite',
    },
    {
      dataField: 'spaceSize',
      formGroup: 'formGroupSpace',
      inputType: 'numeric',
      unit: {
        dataField: 'spaceSizeUOM',
        formGroup: 'formGroupSpace',
        inputType: 'dropdown',
        lookupCategory: AreaUrl,
        displayFn: space => space.getAreaSpaceUOM(),
      },
      displayFn: space => space.getAreaSpace(),
      labelFn: columns => {
        return columns.find(col => col.columnId === LeaseColumns.FloorSuite_SpaceSize)?.header;
      },
    },
    {
      dataField: 'leaseStartDate',
      inputType: 'date',
      formGroup: 'formGroupLeaseDate',
      displayFn: space => {
        const { startDate } = space.getSpaceDates();
        return isMasked(startDate) ? startDate : formatDate(startDate);
      },
      labelFn: columns => {
        return columns.find(col => col.columnId === LeaseColumns.Terms_StartDate)?.header;
      },
    },
    {
      dataField: 'leaseEndDate',
      inputType: 'date',
      formGroup: 'formGroupLeaseDate',
      displayFn: space => {
        const { endDate } = space.getSpaceDates();
        return isMasked(endDate) ? endDate : formatDate(endDate);
      },
      labelFn: columns => {
        return columns.find(col => col.columnId === LeaseColumns.Terms_EndDate)?.header;
      },
    },
    {
      displayFn: space => space.generateTenantRepAgency(),
      labelFn: columns => {
        return columns.find(col => col.columnId === LeaseColumns.Companies_TenantAgency)?.header;
      },
    },
    {
      dataField: 'industryNaicsId',
      inputType: 'naics',
      lookupCategory: '/lookup/naics',
      displayFn: space => space.tenant.ref_industry_classification_desc,
      labelFn: columns => {
        return columns.find(col => col.columnId === LeaseColumns.LeaseCompDetails_SpaceUseNAICS)?.header;
      },
    },
    {
      displayFn: space => space.tenant.ref_property_usage_type_desc,
      inputType: 'dropdown',
      lookupCategory: '/lookup/ref/country?lookupcategory=miq_property_usage',
      labelFn: columns => {
        return columns.find(col => col.columnId === LeaseColumns.Property_SpaceUsageType)?.header;
      },
    },
    {
      dataField: 'rentRate',
      inputType: 'currency',
      formGroup: 'formGroupRentRate',
      unit: {
        dataField: 'rentRateUOM',
        inputType: 'dropdown',
        formGroup: 'formGroupRentRate',
        lookupCategory: '/lookup/ref/country?lookupcategory=currency',
        displayFn: space => space.generateRentRateUOM(),
      },
      displayFn: space => space.generateRentRateValue(),
      labelFn: columns => {
        return columns.find(col => col.columnId === LeaseColumns.Terms_BaseRentMonthly)?.header;
      },
    },
    {
      displayFn: space => (space.lease.id != null ? space.leaseId.toString() : null),
      navigationToEntity: { entity: SuiteType.Lease },
      class: 'link',
      labelFn: columns => {
        return columns.find(col => col.columnId === LeaseColumns.Admin_CompID)?.header;
      },
    },
    {
      displayFn: space => space.getBrandName(),
      navigationToEntity: {
        entity: SuiteType.Brand,
        getEntityField: space => space.getBrandId(),
      },
      class: 'link',
      labelFn: columns => {
        return columns.find(col => col.columnId === LeaseColumns.Brand_BrandName)?.header;
      },
    },
    {
      labelFn: columns => {
        return columns.find(col => col.columnId === LeaseColumns.Brand_BrandCategory)?.header;
      },
      displayFn: space =>
        space.tenant.brand_id != null ? space.tenant.business_category_desc : space.lease.business_category_desc,
    },
    {
      labelFn: columns => {
        return columns.find(col => col.columnId === LeaseColumns.LeaseCompDetails_OwnerOccupied)?.header;
      },
      dataField: 'ownerOccupied',
      inputType: 'dropdown',
      lookupCategory: '/lookup/yn',
      displayFn: space => space.lease.owner_occupied_f,
    },
    {
      label: 'overlayDetails.linkedOpportunities',
      displayFn: space => (space.lease.id != null ? space.leaseId.toString() : null),
      isLinkedOpportunities: true,
    },
  ],
  notesUpdate: [
    {
      labelFn: columns => {
        return columns.find(col => col.columnId === LeaseColumns.LeaseCompDetails_Notes)?.header;
      },
      dataField: 'notes',
      inputType: 'textarea',
      displayFn: space => space.tenant.tenancy_notes,
      class: 'notes',
    },
  ],
  footer: {
    label: 'overlayDetails.lastUpdate',
    class: 'last-update',
    displayFn: space => {
      const { lastUpdatedDate, lastUpdatedBy } = space.getRecordTimestamp();
      return generateLastUpdatedByMessage(lastUpdatedDate, lastUpdatedBy);
    },
  },
};
